import { AdditionalEmail, AdditionalPhone, Residence, SocialProfile } from '@/api/profile/queries';
import { isValidEmail } from '@/utils/validate-email';

import { validatePlatformValue } from './social-platforms';

/**
 * Loose client-side phone validation. The BE applies libphonenumber's `@IsPhoneNumber`
 * (which we can't easily mirror without adding a dep); this gate enforces only:
 *   - leading `+` (country dial code present)
 *   - >= 8 digits total (E.164 minimum is 7; we go slightly above to catch typos)
 * Strict validity remains a BE responsibility.
 */
export const isLooselyValidPhone = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed.startsWith('+')) return false;
  return trimmed.replace(/\D/g, '').length >= 8;
};

const normalizeEmail = (value: string): string => value.trim().toLowerCase();
const normalizePhone = (value: string): string => value.replace(/\D/g, '');
const normalizeSocialKey = (s: SocialProfile): string =>
  `${s.platform}::${s.value.trim().toLowerCase()}`;

export const getEmailRowError = (
  entry: AdditionalEmail,
  idx: number,
  all: AdditionalEmail[],
  primary: string,
): string => {
  const v = normalizeEmail(entry.email);
  if (v.length === 0) return '';
  if (!isValidEmail(entry.email)) return 'Enter a valid email address';
  if (v === normalizeEmail(primary)) return 'This is already your primary email';
  for (let i = 0; i < idx; i++) {
    if (normalizeEmail(all[i].email) === v) return 'Duplicate email';
  }
  return '';
};

export const getPhoneRowError = (
  entry: AdditionalPhone,
  idx: number,
  all: AdditionalPhone[],
): string => {
  const v = normalizePhone(entry.phoneNumber);
  if (v.length === 0) return '';
  if (!isLooselyValidPhone(entry.phoneNumber)) return 'Enter a valid phone number';
  for (let i = 0; i < idx; i++) {
    if (normalizePhone(all[i].phoneNumber) === v) return 'Duplicate phone number';
  }
  return '';
};

export const getSocialRowError = (
  entry: SocialProfile,
  idx: number,
  all: SocialProfile[],
): string => {
  const trimmed = entry.value.trim();
  if (trimmed.length === 0) return '';
  const platformError = validatePlatformValue(entry.platform, trimmed);
  if (platformError) return platformError;
  const key = normalizeSocialKey(entry);
  for (let i = 0; i < idx; i++) {
    if (normalizeSocialKey(all[i]) === key) return 'Duplicate profile for this platform';
  }
  return '';
};

/**
 * Residence-row validity: a user-added entry is valid iff (countryCode is set AND
 * at least one city has a non-empty name) OR (fully empty — will be dropped on Save).
 */
export const isResidenceRowValid = (r: Residence): boolean => {
  const hasCity = r.cities.some((c) => c.name.trim().length > 0);
  if (!r.countryCode && !hasCity) return true;
  return !!r.countryCode && hasCity;
};

export const getResidenceRowError = (r: Residence): string => {
  const hasCity = r.cities.some((c) => c.name.trim().length > 0);
  if (!r.countryCode && !hasCity) return '';
  if (!r.countryCode) return 'Select a country';
  if (!hasCity) return 'Add at least one city';
  return '';
};
